import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Loader2, Upload, FileSpreadsheet, Download, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { toast } from 'sonner';

const UploadLeadsDialog = ({ open, onClose, onSuccess }) => {
  const { api } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/leads/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResult(response.data);
      
      const { created, updated, errors } = response.data;
      if (created > 0 || updated > 0) {
        toast.success(`${created} created, ${updated} updated`);
        // Trigger immediate refresh
        onSuccess();
      } else if (errors > 0) {
        toast.warning('Upload completed with errors - check details below');
      }
    } catch (err) {
      toast.error('Failed to upload leads');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    // Updated template with all mandatory fields (now includes Pin Code)
    const template = `Name,Mobile,Email,State,City,Pin Code,Status,Source,Executive Name,Lead Date
John Doe,9876543210,john@example.com,Maharashtra,Mumbai,400001,New Lead,Website,Test Executive 1,2025-01-18
Jane Smith,9876543211,jane@example.com,Karnataka,Bangalore,560001,Qualified Lead,Referral,Test Executive 2,
Rahul Kumar,9876543212,,Gujarat,Ahmedabad,380001,Nurture,Meta Campaign,,
`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload Leads</DialogTitle>
          <DialogDescription>
            Upload a CSV/Excel file with lead data. Existing leads (by mobile) will be updated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Column Requirements */}
          <div className="p-4 bg-muted/50 rounded-lg space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Column Requirements</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="font-medium text-destructive mb-1">🔴 Mandatory</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• <strong>Name</strong> – Lead name</li>
                  <li>• <strong>Mobile</strong> – Unique identifier</li>
                  <li>• <strong>Status</strong> – Lead status</li>
                  <li>• <strong>Source</strong> – Lead source</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-yellow-600 mb-1">🟡 Optional</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• <strong>Executive Name</strong> – Assignment</li>
                  <li>• <strong>Email</strong></li>
                  <li>• <strong>State</strong></li>
                  <li>• <strong>City</strong></li>
                  <li>• <strong>Pin Code</strong> – 6-digit PIN</li>
                  <li>• <strong>Lead Date</strong></li>
                </ul>
              </div>
            </div>

            <div className="pt-2 border-t text-xs text-muted-foreground">
              <p>✓ Duplicate mobile numbers will UPDATE existing leads</p>
              <p>✓ Invalid rows are skipped (valid rows still imported)</p>
              <p>✓ Status must match: New Lead, Qualified Lead, Virtual Meet, etc.</p>
            </div>
          </div>

          {/* Download Template */}
          <div className="flex items-center justify-between">
            <Label>Download CSV Template</Label>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="mr-2 h-4 w-4" />
              Download Template
            </Button>
          </div>

          {/* File Upload Area */}
          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={handleFileChange}
              className="hidden"
              data-testid="file-input"
            />
            {selectedFile ? (
              <div className="flex items-center justify-center gap-2">
                <FileSpreadsheet className="h-8 w-8 text-primary" />
                <span className="font-medium">{selectedFile.name}</span>
              </div>
            ) : (
              <div>
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Click to select a CSV or Excel file
                </p>
              </div>
            )}
          </div>

          {/* Upload Results */}
          {result && (
            <div className="p-4 bg-muted rounded-lg space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-medium">Upload Complete</span>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-2 bg-green-50 rounded">
                  <p className="text-2xl font-bold text-green-600">{result.created}</p>
                  <p className="text-xs text-muted-foreground">Created</p>
                </div>
                <div className="p-2 bg-blue-50 rounded">
                  <p className="text-2xl font-bold text-blue-600">{result.updated || 0}</p>
                  <p className="text-xs text-muted-foreground">Updated</p>
                </div>
                <div className="p-2 bg-red-50 rounded">
                  <p className="text-2xl font-bold text-red-600">{result.errors}</p>
                  <p className="text-xs text-muted-foreground">Errors</p>
                </div>
              </div>

              {result.errorDetails && result.errorDetails.length > 0 && (
                <div className="mt-3 p-3 bg-red-50 rounded text-sm">
                  <p className="font-medium text-red-700 mb-2 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Error Details:
                  </p>
                  <ul className="space-y-1 text-red-600 max-h-32 overflow-y-auto">
                    {result.errorDetails.map((err, idx) => (
                      <li key={idx} className="text-xs">• {err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.message && (
                <p className="text-sm text-muted-foreground text-center">{result.message}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
              data-testid="upload-leads-btn"
            >
              {uploading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Upload Leads
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadLeadsDialog;
